// Registry of sections rendered inside `ActivityDockExpandedPanel`.
//
// Slice P of `docs/planning/activity_dock_workflow_facilitation.md` §6:
// foundation for the dock's evolution into a workflow facilitation surface.
// Today's content (active-lead checklist, ads activity card, achievements)
// becomes registered entries instead of inline JSX so future slices (daily
// checklist, unsent leads, role-specific dashboards) can append role-gated
// sections without re-touching the expanded panel.
//
// Section ordering = render order top-to-bottom in the expanded panel.
// Role gating is filtering at registry lookup; the dock-level audience gate
// (`DOCK_AUDIENCE_ROLES` enforced by `ActivityFloatingDock`) is the outer
// boundary — only users that pass that gate ever see this panel at all, so
// `"any-dock-audience"` here means "no narrower role contract beyond the
// outer dock gate." Sections with a narrower contract use
// `{ roles: [...] }`, which is checked via `AuthContextValue.hasRole`
// semantics (union across org + platform tokens, impersonation-aware).

import type { DockSectionDefinition } from "./sectionTypes";
import { AdsActivitySection } from "./sections/AdsActivitySection";
import { AchievementsSection } from "./sections/AchievementsSection";
import { ActivityDockActiveLeadChecklistSection } from "./ActivityDockActiveLeadChecklistSection";
import { ActivityDockDailyChecklistSection } from "./sections/ActivityDockDailyChecklistSection";
import { ActivityDockUnsentLeadsSection } from "./sections/ActivityDockUnsentLeadsSection";
import { ActivityDockSentLeadsSection } from "./sections/ActivityDockSentLeadsSection";
import { ActivityDockPlatformContextSection } from "./sections/ActivityDockPlatformContextSection";
import { ActivityDockStaleWacReminderSection } from "./sections/ActivityDockStaleWacReminderSection";
import { ActivityDockCogsPeriodPlaceholderSection } from "./sections/ActivityDockCapabilityPlaceholderSections";
import { ActivityDockLogisticsRouteDepartingSection } from "./sections/ActivityDockLogisticsRouteDepartingSection";
import { ActivityDockProductionStationIdleSection } from "./sections/ActivityDockProductionStationIdleSection";
import { ActivityDockKbGapsSection } from "./sections/ActivityDockKbGapsSection";
import { ActivityDockAiPromptingSuggestionsSection } from "./sections/ActivityDockAiPromptingSuggestionsSection";
import { ActivityDockMessagesSection } from "./sections/ActivityDockMessagesSection";
import { AI_PROMPTING_APPLY_ROLES } from "@/lib/aiPrompting/roles";
import { ADMIN_DOCK_SECTION_ENTRIES } from "./adminDockSectionEntries";
import { PLANT_MATERIAL_REQUEST_DOCK_ENTRIES } from "./plantMaterialRequestDockEntries";
import { OPEN_QC_DOCK_ENTRIES } from "./openQcDockEntries";

// Type re-exports — the type contract for sections lives in
// `sectionTypes.ts` (extracted in RP-6 of
// docs/planning/firefly_role_context_presentation.md once this file
// crossed the 300-line hygiene soft cap). Re-exporting from the
// registry keeps existing consumers (`sectionGate.ts`,
// `useActivityDockSections.ts`, `dockPeekDecision.ts`, tests) on the
// same import path they used pre-extraction. New code can import
// directly from `./sectionTypes` instead.
export type {
  DockSectionDefinition,
  DockSectionPeekContext,
  DockSectionRenderContext,
  DockSectionRoleGate,
  HasRoleFn,
} from "./sectionTypes";

// The canonical, ordered set of sections. Mutating this list is the way to
// add or reorder dock content — `ActivityDockExpandedPanel` only knows how
// to iterate it.
export const DOCK_SECTIONS: readonly DockSectionDefinition[] = [
  {
    // WC-V20-HOST-3 — Stream / team chat host inside Firefly. Cross-bucket;
    // self-hides when org vendor chat is off. Unread drives peek via hasSignal.
    id: "messages",
    roleGate: "any-dock-audience",
    render: () => <ActivityDockMessagesSection />,
    hasSignal: () => false,
  },
  {
    // WF-0 §6.1: sales-only — mirrors ChecklistCard for capture workflow;
    // auto-prompt on lead-id flip (Phase 3). Intentionally duplicates
    // panel-primary checklist (R4) as a glanceable dock companion;
    // primary CTA closes dock so user continues capture in the panel.
    id: "active-lead-checklist",
    roleGate: { roles: ["sales"] },
    render: (ctx) => (
      <ActivityDockActiveLeadChecklistSection onCloseDock={ctx.onCloseDock} />
    ),
    bucketRelevance: ["sales"],
  },
  {
    // Daily sales checklist (Slice C). Sales-role only because the
    // checklist template + metrics are sales-specific. The widget
    // self-shortcircuits when scopedOrgId is null, so platform users
    // who've assumed a `sales` token without an active org won't
    // render an empty card.
    id: "daily-checklist",
    roleGate: { roles: ["sales"] },
    render: (ctx) => (
      <ActivityDockDailyChecklistSection
        onOpenAdsAssistant={ctx.onOpenAdsAssistant}
        onOpenAdsAssistantSuggestion={ctx.onOpenAdsAssistantSuggestion}
        onOpenSalesConsole={ctx.onOpenSalesConsole}
        followUpAttentionCount={ctx.followUpAttentionCount}
      />
    ),
    bucketRelevance: ["sales"],
  },
  {
    // Unsent leads (Slice D). Sales-only — non-sales users don't send
    // leads to GHL so the unsent state is meaningless to them. Renders
    // null internally when `unsentCount === 0`, so on a clean day the
    // section is invisible even for sales users.
    id: "unsent-leads",
    roleGate: { roles: ["sales"] },
    render: (ctx) => (
      <ActivityDockUnsentLeadsSection
        onSendAllUnsent={ctx.onSendAllUnsent}
        isSending={ctx.isSending}
        currentMode={ctx.currentMode}
      />
    ),
    // Slice B2.4: the workflow this section guards (capture →
    // review → send to CRM) lives in `leads` mode and follow-up in
    // `salesConsole`. Restricting peeks to those modes means a sales
    // user who has stepped into `training` or `adsAssistant` for a
    // moment isn't yanked back by an unsent count they already know
    // about — the tab badge keeps the count visible either way.
    modeRelevance: ["leads", "salesConsole"],
    hasSignal: (ctx) => ctx.unsentCount > 0,
    bucketRelevance: ["sales"],
  },
  {
    // Sent past 30 days (Slice B2.3). Sales-only — owner / admin /
    // trainer don't need to see their reps' personal sent history in
    // a small dock section. Renders a collapsed count-only row by
    // default, expands inline on click; query is fetch-on-expand so
    // we don't pay for it when the user isn't looking.
    //
    // WF-0 §6.1: informational-only (expand-to-view history); no
    // peek driver. R3 quiet-day hide when count is known-zero is
    // deferred to §7 L8 — collapsed row stays visible until then.
    // WF-0 §6.1: informational-only (expand-to-view history); no
    // peek driver. R3 quiet-day hide when count is known-zero is
    // deferred to §7 L8 — collapsed row stays visible until then.
    id: "sent-leads-30d",
    roleGate: { roles: ["sales"] },
    render: () => <ActivityDockSentLeadsSection />,
    bucketRelevance: ["sales"],
  },
  {
    id: "ads-activity",
    // RP-3 (closes §7 L5): the previous `"any-dock-audience"` gate
    // rendered a silent ads-activity card to trainer / admin / owner /
    // dev / marketing-only viewers — the section's `hasSignal` already
    // short-circuited to "no peek" for non-sales, but the card itself
    // still appeared in the panel with no actionable state for them.
    // The tightened gate keeps the card visible to the three audiences
    // that actually act on it (union semantics — see
    // `DockSectionRoleGate` JSDoc):
    //   - `sales` reps (they post the ads + own the goal)
    //   - `ads_guidance_editor`s (they curate the ads catalog)
    //   - anyone (e.g. an owner of a small org) who has flipped on the
    //     `is_ads_runner` capability via Settings → Responsibilities
    // Non-sales viewers without the capability no longer see the card
    // at all. An owner-as-coach surface (team-aggregate ads activity)
    // is a future section, not this one — see RP-4+ in the planning
    // doc and §RA-v3 in `firefly_visual_design.md`.
    roleGate: {
      roles: ["sales", "ads_guidance_editor"],
      capabilities: ["is_ads_runner"],
    },
    render: (ctx) => (
      <AdsActivitySection
        onOpenAdsIngestion={ctx.onOpenAdsIngestion}
        onOpenAdsLibrary={ctx.onOpenAdsLibrary}
      />
    ),
    // Slice B2.4: the ads-coaching tone is sales-focused (mirror of
    // `ADS_URGENCY_AUDIENCE_ROLES` in `activityDockStatus.ts`) — an
    // ads_guidance_editor who passes the render gate still won't get
    // peeked at; the `hasRole("sales")` check inside `hasSignal`
    // keeps the urgent-peek path scoped to the rep who owns the
    // daily goal. Scoped to `leads` because that's where the
    // day-to-day ads-capture workflow lives; peeking inside
    // `adsAssistant` while the user is *actively posting* would
    // interrupt the very thing they're doing.
    modeRelevance: ["leads"],
    hasSignal: (ctx) =>
      ctx.hasRole("sales") && (ctx.adsTone === "amber" || ctx.adsTone === "red"),
    bucketRelevance: ["sales"],
  },
  {
    // RP-7 — section stays bucket-agnostic at the registry level
    // (`roleGate: "any-dock-audience"`, `bucketRelevance` omitted),
    // but the rendered gallery now narrows its items to the bucket-
    // relevant categories via the threaded `currentBucket`. See the
    // section component's JSDoc + `src/lib/achievementBucketFilter.ts`
    // for the per-bucket subset map.
    //
    // WF-0 §6.1: gamification exception — no one-click workflow CTA
    // required; never drives peek (no `hasSignal`).
    id: "achievements",
    roleGate: "any-dock-audience",
    render: (ctx) => <AchievementsSection currentBucket={ctx.currentBucket} />,
  },
  {
    // RP-4 — platform-context placeholder. See the section component's
    // JSDoc for the full L3 closure rationale. Gate lists all four
    // platform_* tokens explicitly even though `platform_owner` aliases
    // to the rest via `roleTokenMatches` in `AuthContext.tsx` —
    // explicitness here keeps this list independent of any future
    // refactor of the alias rule.
    id: "platform-context",
    roleGate: {
      roles: [
        "platform_owner",
        "platform_admin",
        "platform_dev",
        "platform_trainer",
      ],
    },
    render: (ctx) => (
      <ActivityDockPlatformContextSection onOpenPlatform={ctx.onOpenPlatform} />
    ),
    bucketRelevance: ["platform"],
  },
  {
    // RP-6 + SC-1 — manufacturing-bucket stale WAC reminder. Count from
    // `/api/mfg/materials/costing-summary` via `useMfgDockSignals`.
    id: "stale-wac-reminder",
    roleGate: { roles: ["manufacturing"] },
    render: (ctx) => (
      <ActivityDockStaleWacReminderSection
        staleWacCount={ctx.staleWacCount ?? null}
        onOpenMfgInventory={ctx.onOpenMfgInventory}
      />
    ),
    bucketRelevance: ["manufacturing"],
    modeRelevance: [
      "mfgInventory",
      "mfgModels",
      "mfgPricing",
      "mfgOverhead",
      "mfgOrders",
      "mfgPlants",
      "mfgBuildPipeline",
    ],
    hasSignal: (ctx) => (ctx.staleWacCount ?? 0) > 0,
  },
  {
    // SC-5 — KB gap inbox (`kb_gap_items` open count). Trainer /
    // ads_guidance_editor via role; admin / owner via `is_kb_author`
    // capability (§5.2 union gate).
    id: "kb-gaps",
    roleGate: {
      roles: ["trainer", "ads_guidance_editor"],
      capabilities: ["is_kb_author"],
    },
    render: (ctx) => (
      <ActivityDockKbGapsSection
        kbGapOpenCount={ctx.kbGapOpenCount ?? null}
        currentMode={ctx.currentMode}
        onOpenKbGapInbox={ctx.onOpenKbGapInbox}
      />
    ),
    bucketRelevance: ["manager"],
    modeRelevance: ["knowledgebase", "trainingAuthor", "locationTools", "aiPrompting"],
    hasSignal: (ctx) => (ctx.kbGapOpenCount ?? 0) > 0,
  },
  {
    // SC-5 — open `ai_prompt_suggestions` awaiting apply-role review.
    id: "ai-prompting-suggestions",
    roleGate: { roles: [...AI_PROMPTING_APPLY_ROLES] },
    render: (ctx) => (
      <ActivityDockAiPromptingSuggestionsSection
        aiPromptingOpenCount={ctx.aiPromptingOpenCount ?? null}
        currentMode={ctx.currentMode}
        onOpenAiPrompting={ctx.onOpenAiPrompting}
      />
    ),
    bucketRelevance: ["manager"],
    modeRelevance: ["aiPrompting", "knowledgebase", "trainingAuthor"],
    hasSignal: (ctx) => (ctx.aiPromptingOpenCount ?? 0) > 0,
  },
  ...ADMIN_DOCK_SECTION_ENTRIES,
  ...PLANT_MATERIAL_REQUEST_DOCK_ENTRIES,
  ...OPEN_QC_DOCK_ENTRIES,
  {
    // §5.7 — first logistics-bucket section. Route departing soon;
    // stub data returns 0 and `hasSignal` is honestly false until
    // `logisticsRoutes` scheduled-window data lands (SC-9).
    id: "logistics-dispatch-reminder",
    roleGate: { capabilities: ["is_dispatcher"] },
    render: () => <ActivityDockLogisticsRouteDepartingSection />,
    bucketRelevance: ["logistics"],
    hasSignal: () => false,
  },
  {
    // §5.6 — first production-bucket section. Station idle past
    // threshold; stub data returns 0 and `hasSignal` is honestly false
    // until `mfgFulfill` station state lands (SC-8).
    id: "production-floor-reminder",
    roleGate: { capabilities: ["is_floor_operator"] },
    render: () => <ActivityDockProductionStationIdleSection />,
    bucketRelevance: ["production"],
    hasSignal: () => false,
  },
  {
    // SC-4 — `is_cogs_author` consumer (stub). Capability-only;
    // period-close work is explicit opt-in (§5.5).
    id: "cogs-period-reminder",
    roleGate: { capabilities: ["is_cogs_author"] },
    render: () => <ActivityDockCogsPeriodPlaceholderSection />,
    bucketRelevance: ["manufacturing"],
  },
];

// Pure gate evaluation (filterDockSectionsByRoles + getDockSectionsForAuth)
// extracted to `sectionGate.ts` in RP-2a to keep this file under the
// file-size hygiene soft cap as the gate type widens.
